import { useEffect, useRef, useState } from "react";
import { useData } from "../Contexts/DataProvider";
import { useSelectedEntity } from "../Contexts/SelectedEntityProvider";
import { useSelectedTab } from "../Contexts/SelectedTabProvider";
import { useSave } from "../hooks/useSave";
import SaveCountdown from "../SaveCountdown/SaveCountdown";
import { buttonClassName, ButtonComponentProps } from "./ButtonHelpers";

const AUTO_SAVE_DURATION = 5 * 60 * 1000;

const SaveButton = (
  props: Omit<ButtonComponentProps, "duration" | "reset" | "onCountdownEnd">
) => {
  const { handleSaveChanges } = useSave();
  const { selectedTab } = useSelectedTab();
  const { selectedEntity } = useSelectedEntity();
  const { data, originalData } = useData();

  const [timeLeft, setTimeLeft] = useState(AUTO_SAVE_DURATION);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const dataRef = useRef(data);
  const originalDataRef = useRef(originalData);

  useEffect(() => {
    dataRef.current = data;
    originalDataRef.current = originalData;
  }, [data, originalData, selectedTab, selectedEntity]);

  const handleTimerInterval = (prev: number) => {
    if (prev <= 1000) {
      clearInterval(timerRef.current!);
      handleSaveChanges(dataRef.current, originalDataRef.current);
      setTimeout(() => {
        setTimeLeft(AUTO_SAVE_DURATION);
        startTimer();
      }, 0);
      return 0;
    }
    return prev - 1000;
  };

  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimeLeft(handleTimerInterval);
    }, 1000);
  };

  useEffect(() => {
    setTimeLeft(AUTO_SAVE_DURATION);
    startTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [selectedTab, selectedEntity]);

  const onClick = () => {
    if (props.isDisabled) return;
    handleSaveChanges();
    setTimeLeft(AUTO_SAVE_DURATION);
    startTimer();
  };

  return (
    <button
      className={buttonClassName(props.selected, props.name, props.isDisabled)}
      onClick={onClick}
    >
      {props.name}
      <SaveCountdown timeLeft={timeLeft} />
    </button>
  );
};

export default SaveButton;
